import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  CircularProgress,
  TableSortLabel
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { useState, useMemo } from 'react'
import { type ClassroomRosterReportModel } from './ClassroomRosterReport'
import * as XLSX from 'xlsx'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

interface TableViewProps {
  reportData: ClassroomRosterReportModel[]
  loading: boolean
}

type SortDirection = 'asc' | 'desc'
type SortableField = keyof ClassroomRosterReportModel

export function ClassroomRosterReportTableView (props: TableViewProps): JSX.Element {
  const { reportData, loading } = props
  const [orderBy, setOrderBy] = useState<SortableField>('lastName')
  const [order, setOrder] = useState<SortDirection>('asc')

  const formatDate = (dateString: string | null): string => {
    if (dateString == null) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  const formatPhone = (phone: string | null): string => {
    if (phone == null || phone.length === 0) return ''
    // Format as (XXX) XXX-XXXX if 10 digits
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 10) {
      return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`
    }
    return phone
  }

  const handleRequestSort = (property: SortableField): void => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedData = useMemo(() => {
    const comparator = (a: ClassroomRosterReportModel, b: ClassroomRosterReportModel): number => {
      let aValue: any = a[orderBy]
      let bValue: any = b[orderBy]

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      // Convert dates to comparable values
      if (orderBy === 'dateOfBirth') {
        aValue = aValue != null ? new Date(aValue).getTime() : 0
        bValue = bValue != null ? new Date(bValue).getTime() : 0
      }

      // String comparison (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.toLowerCase().localeCompare(bValue.toLowerCase())
      }

      // Numeric comparison
      if (aValue < bValue) return -1
      if (aValue > bValue) return 1
      return 0
    }

    return [...reportData].sort((a, b) => {
      return order === 'desc' ? -comparator(a, b) : comparator(a, b)
    })
  }, [reportData, order, orderBy])

  const handleExportToExcel = (): void => {
    const headers = [
      'First Name',
      'Last Name',
      'Date of Birth',
      'Address',
      'City',
      'State',
      'ZIP',
      'FPL Quintile',
      'Caregiver First Name',
      'Caregiver Last Name',
      'Caregiver Email',
      'Caregiver Phone',
      'Developmental Delay',
      'Challenging Behavior',
      'Primary Language Not English',
      'Parent Low Education',
      'Abuse/Neglect',
      'IEP',
      'Service Name',
      'Classroom'
    ]

    const data: Array<Array<string | number>> = sortedData.map((row) => [
      row.firstName,
      row.lastName,
      formatDate(row.dateOfBirth),
      row.address ?? '',
      row.city ?? '',
      row.state ?? '',
      row.zipCode ?? '',
      row.fplQuintile ?? '',
      row.caregiverName ?? '',
      row.caregiverLastName ?? '',
      row.email ?? '',
      formatPhone(row.primaryPhone),
      excelBoolean(row.developmentalDelay),
      excelBoolean(row.challengingBehavior),
      excelBoolean(row.primaryLanguageNotEnglish),
      excelBoolean(row.parentLowEducation),
      excelBoolean(row.abuseNeglect),
      excelBoolean(row.hasIEP),
      row.serviceName,
      row.classroom
    ])

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Classroom Roster')

    XLSX.writeFile(workbook, `ClassroomRosterReport_${new Date().toISOString().split('T')[0]}.xlsx`)
  }

  const excelBoolean = (value: boolean | number): string => {
    if (value === true || value === 1) {
      return 'Y'
    } else {
      return 'N'
    }
  }

  const booleanDisplay = (value: boolean | number | null): JSX.Element => {
    if (value === true || value === 1) {
      return <TableCell sx={{ textAlign: 'center' }}>
        <CheckCircleIcon aria-label="Yes" sx={{ color: '#367575', fontSize: '18px' }} />
      </TableCell>
    } else {
      return <TableCell></TableCell>
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (reportData.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No children found enrolled in the selected classroom and program.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }}>
        <Chip
          label={`${sortedData.length} ${sortedData.length === 1 ? 'child' : 'children'}`}
          sx={{ bgcolor: '#E3F2FD', fontWeight: 'bold' }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
        >
          Download Excel
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small" className="report-table">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'lastName' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'lastName'}
                  direction={orderBy === 'lastName' ? order : 'asc'}
                  onClick={() => { handleRequestSort('lastName') }}
                >
                  Child Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'dateOfBirth' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'dateOfBirth'}
                  direction={orderBy === 'dateOfBirth' ? order : 'asc'}
                  onClick={() => { handleRequestSort('dateOfBirth') }}
                >
                  DOB
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'address' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'address'}
                  direction={orderBy === 'address' ? order : 'asc'}
                  onClick={() => { handleRequestSort('address') }}
                >
                  Address
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'city' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'city'}
                  direction={orderBy === 'city' ? order : 'asc'}
                  onClick={() => { handleRequestSort('city') }}
                >
                  City
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'state' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'state'}
                  direction={orderBy === 'state' ? order : 'asc'}
                  onClick={() => { handleRequestSort('state') }}
                >
                  State
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'zipCode' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'zipCode'}
                  direction={orderBy === 'zipCode' ? order : 'asc'}
                  onClick={() => { handleRequestSort('zipCode') }}
                >
                  ZIP
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'fplQuintile' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'fplQuintile'}
                  direction={orderBy === 'fplQuintile' ? order : 'asc'}
                  onClick={() => { handleRequestSort('fplQuintile') }}
                >
                  FPL Quintile
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'caregiverLastName' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'caregiverLastName'}
                  direction={orderBy === 'caregiverLastName' ? order : 'asc'}
                  onClick={() => { handleRequestSort('caregiverLastName') }}
                >
                  Caregiver
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'email' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => { handleRequestSort('email') }}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'primaryPhone' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'primaryPhone'}
                  direction={orderBy === 'primaryPhone' ? order : 'asc'}
                  onClick={() => { handleRequestSort('primaryPhone') }}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'developmentalDelay' ? order : false} sx={{ textAlign: 'center' }} >
                <TableSortLabel
                  active={orderBy === 'developmentalDelay'}
                  direction={orderBy === 'developmentalDelay' ? order : 'asc'}
                  onClick={() => { handleRequestSort('developmentalDelay') }}
                >
                  Developmental Delay
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'challengingBehavior' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'challengingBehavior'}
                  direction={orderBy === 'challengingBehavior' ? order : 'asc'}
                  onClick={() => { handleRequestSort('challengingBehavior') }}
                >
                  Challenging Behavior
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'primaryLanguageNotEnglish' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'primaryLanguageNotEnglish'}
                  direction={orderBy === 'primaryLanguageNotEnglish' ? order : 'asc'}
                  onClick={() => { handleRequestSort('primaryLanguageNotEnglish') }}
                >
                  Lang Not English
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'parentLowEducation' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'parentLowEducation'}
                  direction={orderBy === 'parentLowEducation' ? order : 'asc'}
                  onClick={() => { handleRequestSort('parentLowEducation') }}
                >
                  Parent Low Education
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'abuseNeglect' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'abuseNeglect'}
                  direction={orderBy === 'abuseNeglect' ? order : 'asc'}
                  onClick={() => { handleRequestSort('abuseNeglect') }}
                >
                  Abuse/Neglect
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'hasIEP' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'hasIEP'}
                  direction={orderBy === 'hasIEP' ? order : 'asc'}
                  onClick={() => { handleRequestSort('hasIEP') }}
                >
                  IEP
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'serviceName' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'serviceName'}
                  direction={orderBy === 'serviceName' ? order : 'asc'}
                  onClick={() => { handleRequestSort('serviceName') }}
                >
                  Service Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'classroom' ? order : false} sx={{ textAlign: 'center' }}>
                <TableSortLabel
                  active={orderBy === 'classroom'}
                  direction={orderBy === 'classroom' ? order : 'asc'}
                  onClick={() => { handleRequestSort('classroom') }}
                >
                  Classroom
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  {row.lastName}, {row.firstName}
                </TableCell>
                <TableCell>{formatDate(row.dateOfBirth)}</TableCell>
                <TableCell>{row.address}</TableCell>
                <TableCell>{row.city}</TableCell>
                <TableCell>{row.state}</TableCell>
                <TableCell>{row.zipCode}</TableCell>
                <TableCell>{row.fplQuintile}</TableCell>
                <TableCell>
                  {row.caregiverLastName != null && row.caregiverName != null
                    ? `${row.caregiverLastName}, ${row.caregiverName}`
                    : ''}
                </TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>{formatPhone(row.primaryPhone)}</TableCell>
                {booleanDisplay(row.developmentalDelay)}
                {booleanDisplay(row.challengingBehavior)}
                {booleanDisplay(row.primaryLanguageNotEnglish)}
                {booleanDisplay(row.parentLowEducation)}
                {booleanDisplay(row.abuseNeglect)}
                {booleanDisplay(row.hasIEP)}
                <TableCell>{row.serviceName}</TableCell>
                <TableCell>{row.classroom}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
